export enum FormContext {
  TEMPLATE = "template",
  JOB_APPLICATION = "job_application",
  JOB_APPLICATION_SUPPLEMENT = "job_application_supplement",
  REFERENCE = "reference",
  REFERENCE_VERIFICATION = "reference_verification",
  RESIDENTIAL_CARE = "residential_care",
}

export enum FieldType {
  INFORMATION = "information",
  DIVIDER = "divider",
  HEADING = "heading",

  TEXT = "text",
  TEXT_AREA = "textarea",
  RADIO_BUTTONS = "radios",
  CHECK_BOXES = "checkboxes",
  DROPDOWN = "dropdown",
  NUMBER = "number",
  YES_NO = "yesno",
  EMAIL_ADDRESS = "emailaddress",
  PHONE_NUMBER = "phonenumber",
  AGREE = "agree",

  DATE_TIME = "datetime",
  GROUP = "group",
  SIGNATURE = "signature",
  REPEATER = "repeater",
  LINKED_FORMS = "linkedforms",
  BODY_MAP = "bodymap",
  VEHICLE_MAP = "vehiclemap",
  FILE = "file",

  RISK_ASSESSMENT = "riskassessment",
  ADDRESSES = "addresses",
  EDUCATION = "education",
  EMPLOYMENTS = "employments",
}

export interface Form {
  id?: string;
  title: string;
  context: FormContext;
  requiresApproval: boolean;
  schemas: SchemaVersion[];

  createdAt: Date;
}

export interface FormSchema {
  id?: string;
  version?: number;
  notes?: string;
  form: Partial<Form>;
  published: boolean;
  publishedAt?: Date;
  fieldLayout: FieldLayout;
}

export interface SchemaVersion {
  id?: string;
  version: number;
  published: boolean;
  publishedAt?: Date;
  active?: boolean;
  updatedAt: Date;
}

export interface FieldLayout {
  id?: string;
  _destroy?: boolean;
  pages: FormPage[];
}

export interface FormPage {
  id: string;
  unsaved?: boolean;
  _destroy?: boolean;
  title: string;
  instructions?: string;
  sortOrder: number;
  rows: FormRow[];
}

export interface FormRow {
  id: string;
  unsaved?: boolean;
  _destroy?: boolean;
  sortOrder: number;
  fields: FormField[];
}

export interface FormFieldSettings {
  // Global
  appearance?: string;
  hideLabel?: boolean;

  // Repeaters, dynamic blocks
  minItems?: number;
  maxItems?: number;

  // Number
  decimalPlaces?: number;

  // Linked Forms
  allowMultiple?: boolean;
  buttonLabel?: string;

  // Addresses & Employments
  minYears?: number;

  // Addresses
  autocompletePostcode?: boolean;

  // Employments
  requireSince?: string;
  allowWeekendGaps?: boolean;

  // File
  kindAudio?: boolean;
  kindCompressed?: boolean;
  kindExcel?: boolean;
  kindHtml?: boolean;
  kindImage?: boolean;
  kindJson?: boolean;
  kindPdf?: boolean;
  kindPowerpoint?: boolean;
  kindText?: boolean;
  kindVideo?: boolean;
  kindWord?: boolean;
  kindXml?: boolean;
}

export interface FormField {
  id: string;
  unsaved?: boolean;
  _destroy?: boolean;
  sortOrder: number;
  fieldType: FieldType;

  label?: string;
  handle: string;
  content?: string;
  instructions?: string;
  placeholder?: string;
  defaultValue?: string;
  nestedFieldLayout?: FieldLayout;
  linkedFormId?: string;

  required?: boolean;
  options?: FormFieldOption[];
  parts?: { [key in FieldPartKey]?: FieldPart };
  settings: FormFieldSettings;

  enableConditions?: boolean;
  conditionsAction?: FieldConditionAction;
  conditionsMatch?: FieldConditionMatch;
  conditions?: FieldConditionRelation[];
}

export type FormFieldError = keyof FormField;

export type FormFieldOption = DropdownOption | CheckboxOption;

export interface DropdownOption {
  id: string;
  label: string;
  value: string;
  default: boolean;
  disabled: boolean;
}

export interface CheckboxOption {
  id: string;
  label: string;
  value: string;
  default: boolean;
  disabled: boolean;
}

export type FieldPartKey =
  | DateFieldPart
  | AddressFieldPart
  | EducationFieldPart
  | EmploymentFieldPositionPart
  | EmploymentFieldGapPart;

export enum DateFieldPart {
  DATE = "date",
  TIME = "time",
}

export enum AddressFieldPart {
  LINE1 = "line1",
  LINE2 = "line2",
  CITY = "city",
  COUNTY = "county",
  POSTCODE = "postcode",
  COUNTRY = "country",
  MONTHS_RESIDENT = "monthsResident",
}

export enum EmploymentFieldPositionPart {
  COMPANY_NAME = "companyName",
  POSITION_TITLE = "positionTitle",
  DESCRIPTION = "description",
  ADDRESS = "address",
  POSTCODE = "postcode",
  MANAGER_NAME = "managerName",
  MANAGER_TELEPHONE = "managerTelephone",
  MANAGER_EMAIL = "managerEmail",
  EMPLOYED_FROM = "employedFrom",
  EMPLOYED_UNTIL = "employedUntil",
  UNTIL_PRESENT = "untilPresent",
  DISCIPLINARY_ACTION = "disciplinaryAction",
  LEAVING_REASON = "leavingReason",
  CONTACT_ALLOWED = "contactAllowed",
}

export enum EmploymentFieldGapPart {
  REASON = "reason",
  START_DATE = "startDate",
  END_DATE = "endDate",
  UNTIL_PRESENT = "untilPresent",
}

export enum EducationFieldPart {
  ESTABLISHMENT_NAME = "establishmentName",
  QUALIFICATION = "qualification",
  START_DATE = "startDate",
  END_DATE = "endDate",
}

export interface FieldPart {
  enabled?: boolean;
  required?: boolean;
  label?: string;
}

export enum FieldCondition {
  EQUALS = "is",
  NOT_EQUALS = "not",
  GREATER_THAN = "gt",
  LESS_THAN = "lt",
  CONTAINS = "contains",
  STARTS_WITH = "starts",
  ENDS_WITH = "ends",
}

export enum FieldConditionAction {
  SHOW = "show",
  HIDE = "hide",
}

export enum FieldConditionMatch {
  ALL = "all",
  ANY = "any",
}

export interface FieldConditionRelation {
  id: string;
  fieldHandle: string;
  condition: FieldCondition;
  value: string;
}

export interface FormFieldReference {
  label: string;
  handle: string;
  fieldType: FieldType;
  options?: FormFieldOption[];
  nested?: {
    label: string;
    handle: string;
    fieldType: FieldType;
    options?: FormFieldOption[];
  }[];
}

export enum FormFieldFileKind {
  AUDIO = "audio",
  COMPRESSED = "compressed",
  EXCEL = "excel",
  HTML = "html",
  IMAGE = "image",
  JSON = "json",
  PDF = "pdf",
  POWERPOINT = "powerpoint",
  TEXT = "text",
  VIDEO = "video",
  WORD = "word",
  XML = "xml",
}
